import DriverForm from '@acorn-portal/shared/src/components/forms/driver/driver-form';
import DriverShortForm from '@acorn-portal/shared/src/components/forms/driver/driver-short-form';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import { Box, Button, Collapse, Grid, styled, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { IManageDriversProps } from './types';

import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import DriverTable, { DriverTableData } from '../../components/table/driver-table';
import { useAppSelector } from '../../hooks/reduxHook';
import { getDriverFormValues } from '../../utils/initialValues/get-driver-form-values';
import {
	DriverShortFormValues,
	getDriverShortFormValues,
} from '../../utils/initialValues/get-driver-short-form-values';
import DLNumberDisclaimer from './dln-number-disclaimer';
import { getDriverFormConfig } from './helpers/get-driver-form-config';
import { getDriverTableData } from './helpers/get-driver-table-data';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { useLocation, useNavigate } from 'react-router';
import isPCCVRealm from '@acorn-portal/shared/src/utils/isPCCV/isPCCV';
import AlertBox from '../alerts/AlertBox';
import { getEnvProduct } from '@acorn-portal/shared/src/utils/get-env-product';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { isTelematics } from '@acorn-portal/shared/src/utils';
import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';

const SmallerBox = styled(Box)(({ theme }) => ({
	paddingRight: '20%',
	paddingLeft: '20%',
	[theme.breakpoints.down('md')]: {
		paddingRight: 'unset',
		paddingLeft: 'unset',
	},
}));

const isPCCV = isPCCVRealm();

// Shared component used across MTA and Renewal driver management pages
const ManageDrivers = (props: IManageDriversProps) => {
	const [previousProps, setPreviousProps] = useState(props);
	const [open, toggleModal] = useState(false);
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const midTermAdjustment = useAppSelector((redux) => redux.midTermAdjustment);
	const navigate = useNavigate();
	const location = useLocation();
	const multiMTAEnabled = useFeatureFlagEnabled(`${getEnvProduct().toLowerCase()}_multi_mta`);
	const isTelematicPolicy = isTelematics(fullPolicy.product);
	const { track } = useTrack();

	useEffect(() => {
		//sets previous props to compare against for changes to occupation
		if (!props.additionalDrivers?.length) return;
		setPreviousProps(props);
	}, [!!props.additionalDrivers?.length]);

	if (location.state) {
		//updates the drivers state to the new drivers and then clears the state
		props.setAdditionalDrivers?.(location.state.drivers);
		navigate(ROUTE_PATHS.makeAChangeDrivers, { replace: true });
	}

	// Driver
	const driver = props.selectedItem;
	const noDriverData = driver === undefined;
	const newDriver = driver?.isNewDriver;
	// Conditional fields and schemas
	const proposerMaritalStatus =
		props.additionalDrivers?.find((driver) => driver.relationshipToProposer === 'P')?.maritalStatus || '';
	const { shortFormSchema, fullFormSchema, formIntro, shortFormExtraFields, fullFormExtraFields, motoringClaimsYears } =
		getDriverFormConfig(fullPolicy.product, proposerMaritalStatus, props.vehicleUse);
	// Initial Values
	const shortFormValues = useMemo(
		() => (driver ? getDriverShortFormValues(driver, shortFormExtraFields) : ({} as DriverShortFormValues)),
		[driver, shortFormExtraFields],
	);
	const fullFormValues = useMemo(
		() => getDriverFormValues(newDriver ? driver : undefined, fullFormExtraFields),
		[newDriver, driver, fullFormExtraFields],
	);
	// Table
	const tableData: DriverTableData = useMemo(
		() => getDriverTableData(props.additionalDrivers),
		[props.additionalDrivers],
	);

	if (props.additionalDrivers === undefined) {
		return null;
	}

	function handleCancel() {
		props.toggleForm(false);
		props.setItem(undefined);
		props.setSelectedItem(undefined);
	}

	const hasEmploymentChanged = () =>
		(['employmentType', 'employmentStatus', 'occupation', 'industry'] as const).some((field) => {
			return props.additionalDrivers?.some((driver, i) => {
				return driver?.[field] !== previousProps.additionalDrivers?.[i]?.[field];
			});
		});

	const canMultiMTA = multiMTAEnabled && isPCCV && !isTelematicPolicy;

	const updateDriverDetails = () => {
		const state = {
			changeType: [...midTermAdjustment.changeType],
			changeOfDriver: true,
			drivers: props.additionalDrivers,
		};

		track(`Manage Drivers - Review/Update Clicked`);
		//updates current page to include the drivers updates incase the user comes back to the page
		navigate(ROUTE_PATHS.makeAChangeDrivers, { state, replace: true });
		//updates the next page to include the drivers updates without affecting mtaChanges, incase the user goes back twice
		navigate(ROUTE_PATHS.makeAChangeUsageAndMileage, { state });
	};

	return (
		<JourneyCardLayout pageTitle='Manage Drivers' loading={props.loading}>
			<Box sx={{ mb: 2 }}>
				<SmallerBox>
					<Grid container direction='row' spacing={1}>
						<Grid size={{ xs: 12, md: 12 }} justifyContent='center' alignContent='center'>
							<DriverTable
								data={tableData}
								disabled={props.showForm}
								onEditClick={props.onEditClick}
								onRemoveClick={props.onRemoveClick}
								onMainClick={props.onSetMainDriver}
							/>
							<Collapse in={!props.showForm}>
								<Box display='flex' justifyContent='flex-end' maxWidth='550px' margin='auto'>
									<Button
										sx={(theme) => ({
											my: 2,
											[theme.breakpoints.down('md')]: {
												width: '100%',
											},
										})}
										variant='contained'
										color='primary'
										onClick={() => props.toggleForm(true)}
										disabled={props.additionalDrivers.length >= 4}
									>
										Add Another Driver
									</Button>
								</Box>
							</Collapse>
						</Grid>
					</Grid>
				</SmallerBox>
				{props.showForm &&
					(noDriverData || newDriver ? (
						<>
							<DLNumberDisclaimer text={props.dlnText} />
							<DriverForm
								initialValues={fullFormValues}
								schema={fullFormSchema}
								introText={formIntro}
								submitText={noDriverData ? 'Add' : 'Save'}
								extraFields={fullFormExtraFields}
								motoringClaimsYears={motoringClaimsYears}
								onSubmit={props.onFullFormSubmit}
								onCancel={handleCancel}
							/>
						</>
					) : (
						<DriverShortForm
							driverDetails={driver}
							initialValues={shortFormValues}
							schema={shortFormSchema}
							extraFields={shortFormExtraFields}
							onSubmit={props.onShortFormSubmit}
							onCancel={handleCancel}
							submitText='Save'
						/>
					))}
				{canMultiMTA && !props.showForm && hasEmploymentChanged() && (
					<SmallerBox sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
						<AlertBox
							severity='warning'
							title='Review your vehicle usage and mileage'
							button={{ children: 'Review/Update', onClick: updateDriverDetails }}
						>
							<Typography variant='body1'>
								Take a moment to reivew/update your vehicle usage and mileage. This helps ensure your policy reflects
								how you use your car and means we can support you more easily if you need to make a claim.
							</Typography>
						</AlertBox>
					</SmallerBox>
				)}
				<HelperModal
					open={props.openDriverModal}
					onClose={() => {
						props.setSelectedItem(undefined);
						props.setItem(undefined);
						props.toggleDriverModal(false);
					}}
					title='Remove Driver'
					description='Are you sure?'
					closeText='Cancel'
					submitText='Delete'
					onSubmit={() => props.onDelete()}
				/>
				<ActionButtons>
					<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
					<ContinueButton onClick={() => props.onPageSubmit()} disabled={props.showForm}>
						{canMultiMTA ? 'Get Quote' : 'Continue'}
					</ContinueButton>
				</ActionButtons>
				<HelperModal
					open={open}
					onClose={() => {
						toggleModal(false);
					}}
					title='Discard The Changes?'
					description='Are you sure you wish to discard the changes?'
					closeText='No'
					submitText='Yes'
					onSubmit={props.onDiscard}
				/>
			</Box>
		</JourneyCardLayout>
	);
};

export default ManageDrivers;
